import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef } from "react";
import FormTextField from "../../components/formFields/FormTextField";
import {
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closed } from "../../components/drawer/drawerSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { STATUSARRAY } from "../../_contstants/common";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import {
  clearMemberShipPlanDetails,
  clearMemberShipPlanID,
} from "./membershipplanSlice";
import {
  createMembershipPlan,
  getAllMemberShipPlans,
  getMembershipPlanById,
  updateMembershipPlan,
} from "./services/membershipplan.services";

const CreateMemberShipPlan: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const [formKey, setFormKey] = React.useState(1);

  const MemberShipPlanDetail = useAppSelector(
    (state) => state.membershipplan.MemberShipPlanDetail
  );
  const MemberShipPlanID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector(
    (state) => state.membershipplan.formLoading
  );

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [MemberShipPlanDetail]);

  useEffect(() => {
    return () => {
      dispatch(clearMemberShipPlanDetails());
      dispatch(clearMemberShipPlanID());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (MemberShipPlanID) {
      dispatch(getMembershipPlanById(MemberShipPlanID));
    }
  }, [MemberShipPlanID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!MemberShipPlanID) {
        const payload = {
          PlanName: values?.PlanName,
          BussinessValue: values?.BussinessValue ? +values?.BussinessValue : 0,
          TicketCount: values?.TicketCount ? +values?.TicketCount : 0,
          DomesticFlightCharge: values?.DomesticFlightCharge
            ? +values?.DomesticFlightCharge
            : 0,
          InternationalFlightCharge: values?.InternationalFlightCharge
            ? +values?.InternationalFlightCharge
            : 0,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createMembershipPlan(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMemberShipPlanDetails());
          dispatch(getAllMemberShipPlans());
        }
      } else {
        const payload = {
          ID: MemberShipPlanID,
          PlanName: values?.PlanName,
          BussinessValue: values?.BussinessValue ? +values?.BussinessValue : 0,
          TicketCount: values?.TicketCount ? +values?.TicketCount : 0,
          DomesticFlightCharge: values?.DomesticFlightCharge
            ? +values?.DomesticFlightCharge
            : 0,
          InternationalFlightCharge: values?.InternationalFlightCharge
            ? +values?.InternationalFlightCharge
            : 0,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateMembershipPlan(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMemberShipPlanDetails());
          dispatch(getAllMemberShipPlans());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={MemberShipPlanDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout gap={{ rows: 10, cols: 10 }} cols={[{ width: "100%" }]}>
              <GridLayoutItem className="master-drawer-header" colSpan={1}>
                <Typography.h4 className="m-0">
                  {MemberShipPlanID
                    ? "Update Membership Plan"
                    : "Create Membership Plan"}
                </Typography.h4>
                <Button
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <RxCross1 />
                </Button>
              </GridLayoutItem>

              <GridLayoutItem colSpan={1}>
                <Field
                  name="PlanName"
                  label="Membership Plan"
                  component={FormTextField}
                  placeholder="Membership Plan"
                  astrike={true}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={1}>
                <Field
                  name="BussinessValue"
                  label="Bussiness Value"
                  component={FormNumericTextField}
                  placeholder="Bussiness Value"
                  astrike={true}
                  min={0}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={1}>
                <Field
                  name="TicketCount"
                  label="Ticket Count"
                  component={FormNumericTextField}
                  placeholder="Ticket Count"
                  astrike={true}
                  min={0}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={1}>
                <Field
                  name="DomesticFlightCharge"
                  label="Domestic Charge"
                  component={FormNumericTextField}
                  placeholder="Domestic Charge"
                  astrike={true}
                  min={0}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={1}>
                <Field
                  name="InternationalFlightCharge"
                  label="International Charge"
                  component={FormNumericTextField}
                  placeholder="International Charge"
                  astrike={true}
                  min={0}
                  validator={requiredValidator}
                />
              </GridLayoutItem>

              <GridLayoutItem colSpan={1}>
                <Field
                  name="IsActive"
                  label="Status"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  options={STATUSARRAY.map((type: any) => {
                    return {
                      value: type.value,
                      label: type.label,
                    };
                  })}
                  placeholder="Status"
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem
                colSpan={1}
                style={{
                  textAlign: "end",
                  marginTop: "10px",
                  width: "100%",
                }}
              >
                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginRight: 4 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
                <ButtonWithLoading
                  label={MemberShipPlanID ? "Update" : "Create"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateMemberShipPlan;
